/* ============================================================
   dentalsmartshop — global.js
   Minimal, dependency-free progressive enhancement.
   Modules: money format, cart (AJAX drawer), mobile nav,
   variant selection, quantity steppers, sticky bar.
   All modules no-op gracefully if their markup is absent.
   ============================================================ */
(function () {
  'use strict';

  var routes = (window.theme && window.theme.routes) || {};
  var cartAdd = routes.cart_add_url || '/cart/add';
  var cartUrl = routes.cart_url || '/cart';
  var cartChange = routes.cart_change_url || '/cart/change';
  var moneyFormat = (window.theme && window.theme.moneyFormat) || '€{{amount}}';

  function formatMoney(cents) {
    var value = (cents / 100).toFixed(2);
    // Spanish formatting: comma decimal separator.
    var valueComma = value.replace('.', ',');
    return moneyFormat
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, valueComma)
      .replace(/\{\{\s*amount\s*\}\}/, value);
  }


  /* ---------- helpers ---------- */
  function on(el, evt, sel, handler) {
    el.addEventListener(evt, function (e) {
      var target = e.target.closest(sel);
      if (target && el.contains(target)) handler(e, target);
    });
  }
  function lockScroll(lock) {
    document.body.classList.toggle('no-scroll', lock);
  }

  /* ============================================================
     CART DRAWER
     ============================================================ */
  var CartDrawer = {
    el: null,
    init: function () {
      this.el = document.querySelector('[data-cart-drawer]');
      if (!this.el) return;
      var self = this;

      // Openers
      document.addEventListener('click', function (e) {
        var opener = e.target.closest('[data-cart-open]');
        if (opener) { e.preventDefault(); self.open(); }
        var closer = e.target.closest('[data-cart-close]');
        if (closer) { e.preventDefault(); self.close(); }
      });

      // Close on ESC
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.el.getAttribute('aria-hidden') === 'false') self.close();
      });

      // Quantity + remove inside drawer
      on(this.el, 'click', '[data-line-change]', function (e, btn) {
        e.preventDefault();
        var line = btn.getAttribute('data-line');
        var qty = parseInt(btn.getAttribute('data-line-change'), 10);
        self.changeLine(line, qty);
      });
    },
    open: function () {
      this.el.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      var focusable = this.el.querySelector('[data-cart-close]');
      if (focusable) focusable.focus();
    },
    close: function () {
      this.el.setAttribute('aria-hidden', 'true');
      lockScroll(false);
    },
    changeLine: function (line, quantity) {
      fetch(cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: parseInt(line, 10), quantity: quantity })
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) { CartDrawer.render(cart); Cart.updateCount(cart.item_count); })
        .catch(function () { window.location.href = cartUrl; });
    },
    render: function (cart) {
      // Ask the drawer section to re-render via Section Rendering API for fidelity.
      var sectionId = this.el.getAttribute('data-section-id');
      if (!sectionId) { window.location.href = cartUrl; return; }
      fetch(cartUrl + '?section_id=' + sectionId)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var fresh = parsed.querySelector('[data-cart-drawer-contents]');
          var current = CartDrawer.el.querySelector('[data-cart-drawer-contents]');
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        });
    }
  };

  /* ============================================================
     CART (add to cart, count badge)
     ============================================================ */
  var Cart = {
    init: function () {
      var self = this;
      document.addEventListener('submit', function (e) {
        var form = e.target.closest('[data-product-form]');
        if (!form) return;
        e.preventDefault();
        self.add(form);
      });
    },
    add: function (form) {
      var btn = form.querySelector('[data-add-button]');
      var original = btn ? btn.innerHTML : '';
      if (btn) { btn.setAttribute('aria-disabled', 'true'); btn.innerHTML = btn.getAttribute('data-adding-text') || 'Añadiendo…'; }

      var formData = new FormData(form);
      formData.append('sections', CartDrawer.el ? CartDrawer.el.getAttribute('data-section-id') : '');

      fetch(cartAdd, {
        method: 'POST',
        headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data.status) { // error
            if (btn) { btn.removeAttribute('aria-disabled'); btn.innerHTML = original; }
            return;
          }
          if (btn) { btn.removeAttribute('aria-disabled'); btn.innerHTML = original; }
          return fetch(cartUrl + '.js').then(function (r) { return r.json(); });
        })
        .then(function (cart) {
          if (!cart) return;
          Cart.updateCount(cart.item_count);
          if (CartDrawer.el) { CartDrawer.render(cart); CartDrawer.open(); }
        })
        .catch(function () {
          if (btn) { btn.removeAttribute('aria-disabled'); btn.innerHTML = original; }
        });
    },
    updateCount: function (count) {
      document.querySelectorAll('[data-cart-count]').forEach(function (node) {
        node.textContent = count;
        node.closest('[data-cart-count-wrap]') &&
          node.closest('[data-cart-count-wrap]').setAttribute('data-count', count);
        node.hidden = count === 0;
      });
    }
  };

  /* ============================================================
     MOBILE NAV DRAWER
     ============================================================ */
  var Nav = {
    init: function () {
      var drawer = document.querySelector('[data-nav-drawer]');
      if (!drawer) return;
      document.addEventListener('click', function (e) {
        if (e.target.closest('[data-nav-open]')) { e.preventDefault(); drawer.setAttribute('aria-hidden', 'false'); lockScroll(true); }
        if (e.target.closest('[data-nav-close]')) { e.preventDefault(); drawer.setAttribute('aria-hidden', 'true'); lockScroll(false); }
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') {
          drawer.setAttribute('aria-hidden', 'true'); lockScroll(false);
        }
      });
    }
  };

  /* ============================================================
     VARIANT SELECTION  (radio-based, updates price / ATC / URL)
     ============================================================ */
  var VariantSelects = {
    init: function () {
      document.querySelectorAll('[data-variant-selects]').forEach(function (root) {
        root.addEventListener('change', function () { VariantSelects.onChange(root); });
      });
    },
    onChange: function (root) {
      var variants = JSON.parse(root.getAttribute('data-variants') || '[]');
      var selected = Array.prototype.map.call(
        root.querySelectorAll('[data-option-selected]:checked, select[data-option-index]'),
        function (input) { return input.value; }
      );
      // Support both radios and selects:
      var chosen = [];
      root.querySelectorAll('[data-option-index]').forEach(function (group) {
        var checked = group.querySelector('input:checked') || group;
        if (checked && checked.value) chosen[parseInt(group.getAttribute('data-option-index'), 10)] = checked.value;
      });
      var match = variants.find(function (v) {
        return v.options.every(function (opt, i) { return opt === chosen[i]; });
      });
      var form = document.querySelector('[data-product-form][data-section="' + root.getAttribute('data-section') + '"]') ||
                 document.querySelector('[data-product-form]');
      if (!match) { VariantSelects.setUnavailable(form); return; }

      // hidden id input
      var idInput = form && form.querySelector('[name="id"]');
      if (idInput) idInput.value = match.id;

      // price
      var priceEl = document.querySelector('[data-price-for="' + root.getAttribute('data-section') + '"]');
      if (priceEl) {
        var html = '<span class="price__current">' + formatMoney(match.price) + '</span>';
        if (match.compare_at_price && match.compare_at_price > match.price) {
          html = '<span class="price__compare">' + formatMoney(match.compare_at_price) + '</span>' + html;
        }
        priceEl.innerHTML = html;
      }
      // ATC state
      var btn = form && form.querySelector('[data-add-button]');
      if (btn) {
        if (match.available) {
          btn.removeAttribute('aria-disabled');
          btn.querySelector('[data-add-text]') &&
            (btn.querySelector('[data-add-text]').textContent = btn.getAttribute('data-default-text') || 'Añadir al carrito');
        } else {
          btn.setAttribute('aria-disabled', 'true');
          btn.querySelector('[data-add-text]') &&
            (btn.querySelector('[data-add-text]').textContent = btn.getAttribute('data-soldout-text') || 'Agotado');
        }
      }
      // URL
      if (history.replaceState && match.id) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', match.id);
        history.replaceState({}, '', url.toString());
      }
    },
    setUnavailable: function (form) {
      var btn = form && form.querySelector('[data-add-button]');
      if (btn) {
        btn.setAttribute('aria-disabled', 'true');
        btn.querySelector('[data-add-text]') &&
          (btn.querySelector('[data-add-text]').textContent = btn.getAttribute('data-unavailable-text') || 'No disponible');
      }
    }
  };

  /* ============================================================
     QUANTITY STEPPERS
     ============================================================ */
  var Quantity = {
    init: function () {
      document.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-qty-change]');
        if (!btn) return;
        e.preventDefault();
        var wrap = btn.closest('[data-qty]');
        var input = wrap && wrap.querySelector('input[type="number"]');
        if (!input) return;
        var step = parseInt(btn.getAttribute('data-qty-change'), 10);
        var min = parseInt(input.getAttribute('min') || '1', 10);
        var value = Math.max(min, (parseInt(input.value, 10) || min) + step);
        input.value = value;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  };

  /* ============================================================
     STICKY BAR — reveal after hero scrolled past
     ============================================================ */
  var Sticky = {
    init: function () {
      var bar = document.querySelector('[data-sticky-bar]');
      if (!bar) return;
      var trigger = document.querySelector('[data-sticky-trigger]') || document.querySelector('#MainProduct');
      if (!trigger || !('IntersectionObserver' in window)) { bar.setAttribute('data-visible', 'true'); return; }
      var io = new IntersectionObserver(function (entries) {
        bar.setAttribute('data-visible', entries[0].isIntersecting ? 'false' : 'true');
      }, { rootMargin: '0px 0px -80% 0px' });
      io.observe(trigger);
    }
  };

  /* ---------- boot ---------- */
  function boot() {
    CartDrawer.init();
    Cart.init();
    Nav.init();
    VariantSelects.init();
    Quantity.init();
    Sticky.init();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init on Theme Editor section load
  document.addEventListener('shopify:section:load', boot);

  window.theme = window.theme || {};
  window.theme.formatMoney = formatMoney;
})();
